import { useMemo } from 'react';
import { callbackEventProvider } from '../services/EventObserver';
import { emitEvent } from '../services/gtm';
import {
  toggleModalType, connectWalletType, toggleLedgerModalType,
  disconnectWalletType, toggleTrezorModalType, getBaseTokenBalanceType,
} from './stateConstants';
import {
  connectWalletProvider, getBalance, onConnectionError, unsubscribeAllEvents,
} from '../services/providerService';
import {
  INPAGE_KEY, LEDGER_LS_KEY, TREZOR_LS_KEY, WALLET_TYPES,
} from '../constants/wallet';
import EventTypes from '../constants/eventTypes';
import { getAccounts } from '../services/utils';
import { IDispatch } from './store';
import { IConnect } from '../index';

export class Actions {
  dispatch: IDispatch;

  constructor(dispatch: IDispatch) {
    this.dispatch = dispatch;
  }

  openModal() {
    this.dispatch({ type: toggleModalType.set, payload: true });
  }

  closeModal(isSuccess?: boolean) {
    this.dispatch({ type: toggleModalType.set, payload: false });
    if (!isSuccess) {
      const error = new Error('User canceled');
      this.dispatch({ type: connectWalletType.failed, payload: error.message });
      callbackEventProvider.emit(EventTypes.CONNECT_ERROR_COMPONENT, error);
    }
  }

  closeLedgerFunnel() {
    this.dispatch(toggleLedgerModalType.emit(false));
  }

  closeTrezorFunnel() {
    this.dispatch(toggleTrezorModalType.emit(false));
  }

  async connectWallet(opts: IConnect) {
    callbackEventProvider.emit(EventTypes.CONNECT_REQUEST_COMPONENT, opts);
    const { connectionType } = opts;
    this.dispatch({ type: connectWalletType.request, payload: connectionType });
    const ledgerPathStrorage = localStorage.getItem(LEDGER_LS_KEY);
    const trezorPathStorage = localStorage.getItem(TREZOR_LS_KEY);
    const options: IConnect = { ...opts, ledgerPath: opts.ledgerPath || ledgerPathStrorage, trezorPath: opts.trezorPath || trezorPathStorage };
    try {
      if (connectionType === WALLET_TYPES.ledger && !options.ledgerPath) {
        this.disconnect(false);
        this.dispatch(toggleLedgerModalType.emit(true));
      } else if (connectionType === WALLET_TYPES.trezor && !options.trezorPath) {
        this.disconnect(false);
        this.dispatch(toggleTrezorModalType.emit(true));
      } else {
        unsubscribeAllEvents();
        const {
          provider, connector, ledgerPath, trezorPath, providerName,
        } = await connectWalletProvider(options);
        emitEvent({
          event: 'connected',
          connectionType: providerName || connectionType,
          rpcUrl: options.rpcUrl,
          networkId: options.networkId,
        });
        localStorage.setItem('web3WalletConnect', connectionType);
        const account = await getAccounts(provider);
        this.getBaseTokenBalance(account).catch(console.warn);
        const eventPayload = {
          provider, connectionType, connector, account, ledgerPath, trezorPath, providerName,
        };
        callbackEventProvider.emit(EventTypes.CONNECT_SUCCESS_COMPONENT, eventPayload);
        callbackEventProvider.emit(EventTypes.CONNECT_SUCCESS_MODULE, eventPayload);
        this.dispatch({
          type: connectWalletType.success,
          payload: {
            provider, account, connectionType, networkId: opts.networkId, providerName,
          },
        });
        if (opts.removeLSKey) {
          localStorage.removeItem(INPAGE_KEY);
        }
        this.closeModal(true);
      }
    } catch (err) {
      onConnectionError(connectionType);
      this.dispatch({ type: connectWalletType.failed, payload: err.message });
      callbackEventProvider.emit(EventTypes.CONNECT_ERROR_COMPONENT, err);
    }
  }

  disconnect(isCloseModal?: boolean) {
    unsubscribeAllEvents();
    this.dispatch({ type: disconnectWalletType.set });
    localStorage.removeItem(INPAGE_KEY);
    callbackEventProvider.emit(EventTypes.DISCONNECT, null);
    if (isCloseModal) {
      this.closeModal(true);
    }
  }

  async getBaseTokenBalance(address: string) {
    this.dispatch({ type: getBaseTokenBalanceType.request });
    try {
      const payload = await getBalance(address);
      this.dispatch({
        type: getBaseTokenBalanceType.success,
        payload,
      });
    } catch (err) {
      this.dispatch({
        type: getBaseTokenBalanceType.failed,
        payload: err.message,
      });
    }
  }
}

const useActionsHook = (dispatch: IDispatch) => useMemo(() => new Actions(dispatch), [dispatch]);

export default useActionsHook;
